import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Target,
  BarChart3,
  Sparkles,
  Layers,
  ShieldCheck,
  Globe2,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import TestimonialsCarousel from "../components/TestimonialsCarousel";

const STEPS = [
  {
    icon: Layers,
    title: "1. Tell us about you",
    body: "Pick your field, the technical and soft skills you actually have, then your experience for context. Three short steps, ~4 minutes.",
  },
  {
    icon: Target,
    title: "2. We score every role",
    body: "54 IT roles get a clear 0-100 Skills Match — driven by your technical + soft skills only. Migrants and graduates are never penalised on the headline.",
  },
  {
    icon: ShieldCheck,
    title: "3. Guardrails kick in",
    body: "A role only earns 'Primary' if your Skills Match is 80+, technical fit is 60+ and 70%+ of mandatory skills are covered. No false promotions.",
  },
  {
    icon: Sparkles,
    title: "4. You get a coach-friendly plan",
    body: "Primary, Secondary, Stretch and longer-term targets — each with missing mandatory skills, the single skill to add next, and a plain-English coach verdict.",
  },
];

const FEATURES = [
  {
    icon: Globe2,
    title: "Built for the Australian IT market",
    body: "Local-experience bands, AU-specific demand scores and role families calibrated to what hiring managers in Sydney, Melbourne, Brisbane and Canberra actually want.",
  },
  {
    icon: BarChart3,
    title: "Live market demand baked in",
    body: "Demand is a positive ranking booster — Very High +8, High +5, Moderate +2 — added on top of your Skills Match for ordering. You only chase roles worth chasing right now, but low demand never punishes your score.",
  },
  {
    icon: Target,
    title: "Mandatory vs Important vs Nice-to-Have",
    body: "Skill matches are weighted by priority. Missing a mandatory skill costs more than missing a nice-to-have — exactly how recruiters think.",
  },
];

const COMPANIES = [
  "UniSuper", "RMIT", "NAB", "EY", "IAG", "Bupa", "NBN",
  "Monash University", "RACQ", "VicRoads", "PlanIt", "VicSuper",
];

const Landing = () => {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 grain-texture opacity-70" />
        <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02] text-slate-900 font-display"
              data-testid="hero-title"
            >
              Stop guessing which role to apply for.{" "}
              <span className="italic text-amber-600 font-black">
                Score every option in 4 minutes.
              </span>
            </h1>
            <p
              className="mt-6 max-w-xl text-slate-600 text-lg leading-relaxed"
              data-testid="hero-subtitle"
            >
              Mattoo RoleFinder maps your skills against <b>54 IT roles</b>{" "}
              and gives you a clear <b>Skills Match</b> — a focused score that
              recruiters can defend. You walk away knowing your best-fit role,
              your stretch options, and the single skill to add next.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/assessment"
                data-testid="start-assessment-btn"
                className="group inline-flex items-center gap-2 rounded-md bg-slate-900 text-white px-6 py-3.5 text-sm font-semibold hover:bg-slate-800 transition-colors"
              >
                Start the 3-step assessment
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-0.5 transition-transform"
                />
              </Link>
              <div
                data-testid="hero-price-badge"
                className="inline-flex flex-col gap-1"
              >
                <span className="inline-flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-base font-bold">
                  <span className="text-slate-400 line-through font-semibold">
                    $197
                  </span>
                  <span className="text-amber-600 tracking-wide">FREE</span>
                </span>
                <span className="text-[11px] text-slate-500 leading-snug max-w-[260px]">
                  Free during launch — normally part of our $197 Profile
                  Optimisation toolkit.
                </span>
              </div>
              <a
                href="#how-it-works"
                data-testid="how-it-works-link"
                className="text-sm font-medium text-slate-700 hover:text-slate-900 underline-offset-4 hover:underline"
              >
                How does it work?
              </a>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-slate-600">
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 size={16} className="text-emerald-700" />
                Takes ~4 minutes
              </span>
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 size={16} className="text-emerald-700" />
                Coach-friendly results, not a personality quiz
              </span>
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 size={16} className="text-emerald-700" />
                Backed by Mattoo's 100% satisfaction guarantee
              </span>
            </div>
          </div>

          {/* Hero visual - a stylized preview of the role result card */}
          <div className="lg:col-span-5 animate-fade-up delay-2">
            <div className="relative">
              <div className="absolute -top-6 -left-6 h-40 w-40 rounded-full bg-amber-100/60 blur-3xl" />
              <div className="absolute -bottom-8 -right-8 h-48 w-48 rounded-full bg-emerald-100/50 blur-3xl" />

              {/* Mock result card showing what the tool produces */}
              <div className="relative rounded-2xl border border-slate-200 bg-white p-6 lg:p-7 shadow-xl shadow-slate-900/5">
                <div className="flex items-center justify-between gap-3">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-700">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-600 animate-pulse" />
                    Sample result · Primary fit
                  </span>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-700 text-white">
                    Primary
                  </span>
                </div>

                <h3 className="mt-3 font-display text-2xl font-black text-slate-900 leading-tight">
                  Senior Business Analyst
                </h3>
                <p className="text-xs text-slate-500 font-semibold mt-0.5">
                  Business &amp; Product · Demand: Very High
                </p>

                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-display text-5xl font-black text-slate-900 leading-none">
                    91
                  </span>
                  <span className="text-sm font-bold text-slate-400">/ 100</span>
                  <span className="ml-auto text-xs font-mono text-emerald-700 font-semibold">
                    ↑ 12 above secondary
                  </span>
                </div>

                {/* Mini Skills-Match drivers (Technical + Soft only).
                    Experience and Local-AU now live in Market Readiness
                    on the Results page — they are not score drivers. */}
                <div className="mt-5 space-y-2.5">
                  {[
                    { label: "Technical", v: 94, w: "w-[94%]" },
                    { label: "Soft", v: 88, w: "w-[88%]" },
                    { label: "Demand", v: 100, w: "w-full" },
                  ].map((b) => (
                    <div key={b.label} className="flex items-center gap-3 text-[11px]">
                      <span className="w-20 uppercase tracking-wider font-semibold text-slate-500 shrink-0">
                        {b.label}
                      </span>
                      <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className={`${b.w} h-full bg-slate-900 rounded-full`} />
                      </div>
                      <span className="w-8 text-right font-mono font-bold text-slate-900">
                        {b.v}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-5 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2.5 text-[12px] text-emerald-900 leading-relaxed">
                  <span className="font-semibold">Suitable to apply now.</span>{" "}
                  Strong technical match · 1 mandatory skill to close
                  (Stakeholder Management).
                </div>
              </div>

              {/* Floating mini cards */}
              <div className="absolute -top-4 right-4 lg:right-8 rotate-3 rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-md hidden md:block">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  Stretch
                </p>
                <p className="text-sm font-bold text-slate-900">
                  Solution Architect · 67
                </p>
              </div>
              <div className="absolute -bottom-4 left-4 lg:left-8 -rotate-2 rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-md hidden md:block">
                <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">
                  Top gap
                </p>
                <p className="text-sm font-bold text-slate-900">
                  Stakeholder Management
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted-by strip */}
      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 text-center">
            Mattoo clients have landed roles at
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            {COMPANIES.map((c) => (
              <span
                key={c}
                className="text-slate-700 font-display font-semibold text-base lg:text-lg tracking-tight"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section
        id="how-it-works"
        className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-20"
      >
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">
            How it works
          </p>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 font-display">
            A transparent recommendation grounded in your real skills —
            <span className="text-amber-600"> not a personality quiz.</span>
          </h2>
          <p className="mt-3 text-slate-600 max-w-2xl">
            Every score is explainable. Every component is editable in admin.
            Every guardrail is documented. Built so coaches can defend the
            output in a 1:1 conversation.
          </p>
        </div>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={s.title}
                data-testid={`how-step-${i}`}
                className="rounded-lg border border-slate-200 bg-white p-6 hover:-translate-y-1 hover:shadow-lg transition-all"
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-slate-900 text-white">
                  <Icon size={18} />
                </span>
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  {s.body}
                </p>
              </div>
            );
          })}
        </div>

        {/* Scoring formula card removed — keep messaging high-level */}
      </section>

      {/* Features */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 max-w-2xl">
            Designed for migrants, career-changers and up-skillers in
            Australia.
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  data-testid={`feature-card-${i}`}
                  className="rounded-lg border border-slate-200 bg-slate-50 p-6"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-slate-900 text-white">
                    <Icon size={18} />
                  </span>
                  <h3 className="mt-4 font-display text-xl font-semibold text-slate-900">
                    {f.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {f.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials carousel */}
      <TestimonialsCarousel />

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-20">
        <div className="rounded-xl border border-slate-200 bg-slate-900 text-white p-8 lg:p-12 grid lg:grid-cols-3 gap-6 items-center">
          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-400">
              <span className="line-through text-slate-500 mr-2">$197</span>
              Free during launch · 4 minutes
            </p>
            <h3 className="mt-2 font-display text-2xl sm:text-3xl font-black">
              Find the role you should actually apply for.
            </h3>
            <p className="mt-2 text-slate-300 max-w-xl">
              Stop firing applications into silence. Get a clear Primary,
              Secondary and Stretch — with the exact mandatory skills you need
              to close. Backed by Mattoo's 100% satisfaction money-back
              guarantee.
            </p>
          </div>
          <div className="lg:text-right">
            <Link
              to="/assessment"
              data-testid="cta-assessment-btn"
              className="inline-flex items-center gap-2 rounded-md bg-amber-500 text-slate-900 px-5 py-3 text-sm font-bold hover:bg-amber-400 transition-colors"
            >
              Start the assessment
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
